<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">
      <div class="bg-white rounded-lg shadow-lg overflow-hidden p-6">
        <h1 class="text-xl font-bold mb-4 flex items-center">
          <v-icon name="co-map" class="mr-2 text-blue-600" /> {{ $t('pv.gestione.history_title') }}
        </h1>
        <div v-if="travels.last_page > 1" class="flex items-center justify-between mb-6 pt-4 border-t border-gray-100">
          <span class="text-xs text-gray-500">
            {{ $t('pv.gestione.page_summary', { current: travels.current_page, last: travels.last_page, total: travels.total }) }}
          </span>
          <div class="flex gap-2">
            <button
              @click="changePage(travels.current_page - 1)"
              :disabled="travels.current_page === 1"
              class="px-3 py-1.5 text-xs font-bold bg-white border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">
              {{ $t('pv.gestione.previous') }}
            </button>
            <button
              v-for="p in paginationRange" :key="p"
              @click="p !== '...' && changePage(p)"
              :disabled="p === '...'"
              class="px-3 py-1.5 text-xs font-bold rounded-lg border"
              :class="p === travels.current_page
                ? 'bg-blue-600 text-white border-blue-600'
                : p === '...'
                ? 'bg-white border-gray-300 cursor-default'
                : 'bg-white border-gray-300 hover:bg-gray-50'">
              {{ p }}
            </button>
            <button
              @click="changePage(travels.current_page + 1)"
              :disabled="travels.current_page === travels.last_page"
              class="px-3 py-1.5 text-xs font-bold bg-white border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">
              {{ $t('pv.gestione.next') }}
            </button>
          </div>
        </div>
        <div class="space-y-4">
          <div v-for="travel in travels.data" :key="travel.id" class="border rounded-lg overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md">
            <div @click="toggleTravel(travel.id)" class="bg-gray-50 p-4 cursor-pointer flex items-center justify-between hover:bg-gray-100">
              <div class="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
                <div>
                  <label class="text-[10px] uppercase font-bold text-gray-400 block">{{ $t('pv.gestione.field_date') }}</label>
                  <span class="text-sm font-semibold">{{ formatDate(travel.travel_date) }}</span>
                </div>
                <div class="col-span-1 md:col-span-2">
                  <label class="text-[10px] uppercase font-bold text-gray-400 block">{{ $t('pv.gestione.field_travel_name') }}</label>
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-semibold text-blue-600">{{ travel.travel_name }}</span>
                    <!-- Piccolo indicatore se ci sono opzioni attive -->
                    <v-icon v-if="checkActiveOptions(travel.travel_options)" name="hi-adjustments" scale="0.7" class="text-blue-400" :title="$t('pv.gestione.custom_options_active')" />
                  </div>
                </div>
                <div class="text-center md:text-right">
                  <label class="text-[10px] uppercase font-bold text-gray-400 block">{{ $t('pv.gestione.field_distance_stops') }}</label>
                  <span class="text-sm font-bold">{{ travel.total_km }} km / {{ travel.rows?.length || 0 }}</span>
                </div>
              </div>
              
              <div class="ml-4 flex items-center gap-2">
                <v-icon :name="expandedTravelId === travel.id ? 'hi-chevron-up' : 'hi-chevron-down'" class="text-gray-400" />
              </div>
            </div>


            <div v-show="expandedTravelId === travel.id" class="p-4 border-t bg-white">
              <!-- Header info veloci dentro l'accordion -->
              <div class="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-100">
                <div class="flex items-center gap-4">
                    <div class="bg-blue-50 px-3 py-1 rounded-full">
                        <span class="text-[10px] font-bold text-blue-600 uppercase mr-2">{{ $t('pv.gestione.carrier_label') }}</span>
                        <span class="text-xs font-medium text-blue-800">{{ travel.carrier_id }}</span>
                    </div>
                    <!-- Opzioni di Viaggio in linea dentro l'accordion -->
                    <div class="flex items-center gap-2">
                        <span class="text-[10px] font-bold text-gray-400 uppercase">{{ $t('pv.gestione.route_config_label') }}</span>
                        <div class="flex gap-1">
                            <template v-if="checkActiveOptions(travel.travel_options)">
                                <span v-if="getOptions(travel.travel_options)?.optimize_route" class="bg-green-600 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.gestione.tag_optimized') }}</span>
                                <span v-if="getOptions(travel.travel_options)?.avoid_tolls" class="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.gestione.tag_no_tolls') }}</span>
                                <span v-if="getOptions(travel.travel_options)?.avoid_highways" class="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.gestione.tag_no_highways') }}</span>
                                <span v-if="getOptions(travel.travel_options)?.avoid_ferries" class="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.gestione.tag_no_ferries') }}</span>
                            </template>
                            <span v-else class="text-[10px] text-gray-400 italic font-medium uppercase tracking-wider">{{ $t('pv.gestione.standard_options') }}</span>
                        </div>
                    </div>
                </div>
              </div>
              <div class="flex flex-col lg:flex-row gap-6">
                <div class="static-map-container w-full lg:w-1/3">
                  <img :src="getStaticMapUrl(travel)" :alt="$t('pv.gestione.modal_route_stops')" class="rounded-lg shadow-inner border border-gray-200 w-full h-auto object-cover" loading="lazy" />
                </div>

                <div class="flex-1">
                  <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 h-full">
                    <div class="flex-1">
                      <h4 class="text-xs font-bold text-gray-700 uppercase mb-2">{{ $t('pv.gestione.stops_detail') }}</h4>
                      <div class="flex flex-col gap-1">
                        <div v-for="(row, idx) in travel.rows" :key="row.id" class="group flex flex-wrap items-center gap-2 text-[11px] bg-white p-1.5 rounded-lg border border-gray-200 shadow-sm hover:border-blue-300 transition-all">
                          <div class="flex items-center justify-center w-5 h-5 rounded-md bg-blue-50 text-blue-600 font-bold border border-blue-100 shrink-0 text-[10px]">
                            {{ idx + 1 }}
                          </div>
                          <div class="flex-1 min-w-[150px]">
                            <div class="font-bold text-gray-900 leading-tight">{{ row.company_name }}</div>
                            <div v-if="getCliCodesFromRow(row)" class="text-[10px] text-blue-600 font-medium">{{ getCliCodesFromRow(row) }}</div>
                            <div v-if="row.address" class="text-[10px] text-gray-400 truncate" :title="row.address">{{ row.address }}</div>
                          </div>
                          <div class="flex flex-wrap items-center gap-2">
                            <div class="flex items-center gap-1 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                              <span class="text-gray-400 font-medium uppercase text-[8px]">{{ $t('pv.gestione.packages_short') }}</span>
                              <span class="font-bold text-gray-700">{{ row.colli ?? '0' }}</span>
                            </div>
                            <div v-if="row.notes" class="flex items-center gap-1 text-gray-600 italic">
                              <v-icon name="hi-annotation" scale="0.5" class="text-gray-400" />
                              <span class="truncate max-w-[120px]">{{ row.notes }}</span>
                            </div>
                            <div v-if="getTelephonFromRow(row)" class="flex items-center gap-1 text-blue-600 font-bold">
                              <v-icon name="hi-phone" scale="0.5" class="text-blue-400" />
                              <span>{{ getTelephonFromRow(row) }}</span>
                            </div>
                            
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-col gap-2 self-end md:self-center min-w-[140px]">
                      <button @click.stop="viewTravel(travel)" class="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-blue-700 flex items-center gap-2 shadow-sm transition-all justify-center">
                        <v-icon name="co-map" scale="0.8" /> {{ $t('pv.gestione.btn_edit_route') }}
                      </button>
                      <button @click.stop="printTravel(travel)" class="bg-gray-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-gray-700 flex items-center gap-2 shadow-sm transition-all justify-center">
                        <v-icon name="co-print" scale="0.8" /> {{ $t('pv.gestione.btn_print') }}
                      </button>
                      <button @click.stop="deleteTravel(travel)" :disabled="isLoading" class="bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-red-700 flex items-center gap-2 shadow-sm transition-all justify-center">
                        <v-icon name="co-trash" scale="0.8" /> {{ $t('pv.gestione.btn_delete_route') }}
                      </button>
                      <button @click.stop="exportExcel(travel)" :disabled="isLoading" class="bg-orange-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-orange-700 flex items-center gap-2 shadow-sm transition-all justify-center">
                        <v-icon name="bi-download" scale="0.8" /> {{ $t('pv.gestione.btn_export_excel') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="travels.data.length === 0" class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <v-icon name="co-map" scale="2" class="text-gray-300 mb-2" />
          <p class="text-gray-500 italic">{{ $t('pv.gestione.no_saved_itineraries') }}</p>
        </div>
      </div>
    </div>
    


    <div v-if="showMapModal" class="fixed left-0 right-0 bottom-0 z-50" style="top: 80px;">
      <div class="bg-white w-full h-full overflow-hidden flex flex-col">
        <div class="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 px-4 flex justify-between items-center">
          <h2 class="text-lg font-bold flex items-center">
            <v-icon name="co-map" class="mr-2" /> {{ selectedTravel?.travel_name }} - {{ $t('pv.gestione.modal_edit_route_title') }}
          </h2>
          <button @click="closeMapModal" class="text-white hover:text-gray-200">
            <v-icon name="co-x" scale="1.5" />
          </button>
        </div>

        <div class="flex flex-col lg:flex-row overflow-hidden min-h-0 flex-1">
          <div class="w-full lg:w-1/4 border-b lg:border-b-0 lg:border-r border-gray-200 p-2 bg-gray-50 overflow-y-auto max-h-[30vh] lg:max-h-none">
            <h3 class="text-sm font-bold mb-3 text-gray-700">{{ $t('pv.gestione.modal_route_stops') }} ({{ waypoints.length }})</h3>
            <div class="mb-4 bg-white p-3 rounded-lg shadow-sm border border-gray-200">
              <label class="block text-xs font-bold text-gray-700 mb-1 uppercase">{{ $t('pv.shared.add_waypoint') }}</label>
              <div class="flex gap-2">
                <input ref="newWaypointInput" type="text" :placeholder="$t('pv.shared.search_address_placeholder')" class="flex-1 text-xs border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" :disabled="isLoading" />
                <button @click="addNewWaypoint" class="bg-blue-600 text-white px-3 rounded hover:bg-blue-700 disabled:opacity-50" :disabled="!tempPlace || isLoading">
                  <v-icon name="co-plus" scale="0.8" />
                </button>
              </div>
            </div>

            <draggable v-model="waypoints" item-key="id" @end="onDragEnd" class="space-y-2" handle=".drag-handle" :disabled="isLoading" :animation="200" ghost-class="drag-ghost" chosen-class="drag-chosen">
              <template #item="{ element: wp, index }">
                <div class="bg-white rounded-lg p-1 shadow-sm border-l-4 relative group"
                  :class="{ 'border-green-500': index === 0, 'border-red-500': index === waypoints.length - 1, 'border-blue-500': index > 0 && index < waypoints.length - 1 }">
                  <button 
                    @click="removeWaypoint(index)"
                    class="absolute top-2 right-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                    :title="$t('pv.shared.delete_waypoint_title')"
                    :disabled="isLoading"
                  >
                    <v-icon name="co-x" scale="0.8" />
                  </button> 
                  
                  
                  <div class="flex items-center gap-2">
                    <div class="drag-handle text-gray-400 hover:text-gray-600 shrink-0" :class="isLoading ? 'cursor-not-allowed' : 'cursor-move'">≡</div>
                    <span class="shrink-0 font-bold text-xs">
                      {{ index === 0 ? '🚩' : index === waypoints.length - 1 ? '🏁' : index }}
                    </span>
                    <div class="min-w-0 flex-1">
                      <div class="text-[10px] text-gray-600 truncate" :title="((wp.allCli && wp.allCli.length ? (usa_categoria_cliente && wp.Category === usa_categoria_cliente ? wp.allCliSede : wp.allCli).join(', ') + ' · ' : '') + wp.address)">
                        <span v-if="wp.allCli && wp.allCli.length" class="font-bold text-blue-600">{{ (usa_categoria_cliente && wp.Category === usa_categoria_cliente ? wp.allCliSede : wp.allCli).join(', ') }} · </span>{{ wp.address }}
                      </div>
                      <div v-if="getCliCodesFromRow(wp)" class="text-[10px] text-blue-600 font-medium truncate" :title="getCliCodesFromRow(wp)">
                        {{ getCliCodesFromRow(wp) }}
                      </div>
                      <div v-if="wp.telephon || wp.avvertimenti" class="text-gray-400 text-[10px] truncate" :title="[wp.telephon, wp.avvertimenti].filter(Boolean).join(' | ')">
                        {{ [wp.telephon, wp.avvertimenti].filter(Boolean).join(' | ') }}
                      </div>
                    </div>
                  </div>
                  
                </div>
              </template>
            </draggable>
          </div>
          
          <div class="flex-1 relative min-h-[40vh] lg:min-h-0">
            <button @click="showOptionsPanel = !showOptionsPanel" 
              class="absolute top-2 mt-5 right-6 z-20 bg-white p-3 rounded-lg shadow-md hover:bg-gray-100 border rotate-90">
              <v-icon name="hi-switch-horizontal" class="rotate-90" /> &nbsp; {{ $t('pv.shared.options') }}
            </button>
            <div v-if="isCalculatingRoute || isLoading" class="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
              <div class="text-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p class="mt-2 text-xs text-gray-600">{{ isLoading ? $t('pv.shared.saving') : $t('pv.shared.calculating') }}</p>
              </div>
            </div>
            <RouteEditorMapHere
              v-if="useHereMap"
              ref="routeMap"
              :api-key="routing_provider_key"
              :waypoints="waypoints"
              :center="mapCenter"
              :zoom="10"
              :avoid-tolls="avoidTolls"
              :avoid-highways="avoidHighways"
              :avoid-ferries="avoidFerries"
              :optimize-route="optimizeRoute"
              @ready="onMapReady"
              @route-calculated="onRouteCalculated"
              @route-error="onRouteError"
              @waypoints-optimized="onWaypointsOptimized"
              @adjacent-duplicates-detected="onAdjacentDuplicatesDetected"
              style="width: 100%; height: 100%;"
            />

            <RouteEditorMapGoogle
              v-else
              ref="routeMap"
              :api-key="routing_provider_key"
              :waypoints="waypoints"
              :center="mapCenter"
              :zoom="10"
              :avoid-tolls="avoidTolls"
              :avoid-highways="avoidHighways"
              :avoid-ferries="avoidFerries"
              :optimize-route="optimizeRoute"
              @ready="onMapReady"
              @route-calculated="onRouteCalculated"
              @route-error="onRouteError"
              @waypoints-optimized="onWaypointsOptimized"
            />
          </div>

          <div v-show="showOptionsPanel" class="relative right-0 top-0 bottom-0 w-full lg:w-1/4 p-2 lg:p-4 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto z-30 shadow-xl max-h-[40vh] lg:max-h-none">
            <h3 class="text-sm font-semibold mb-3 text-gray-700 uppercase">{{ $t('pv.shared.options') }}</h3>
            <div class="space-y-4">
              <div class="bg-white rounded-lg p-3 shadow-sm border">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="avoidTolls" @change="calculateRoute(true)" class="h-4 w-4 text-blue-600 mr-3" />
                  <div>
                    <div class="font-semibold text-xs text-gray-800">{{ $t('pv.shared.avoid_tolls') }}</div>
                  </div>
                </label>
              </div>
              <div class="bg-white rounded-lg p-3 shadow-sm border">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="avoidHighways" @change="calculateRoute(true)" class="h-4 w-4 text-blue-600 mr-3" />
                  <div>
                    <div class="font-semibold text-xs text-gray-800">{{ $t('pv.shared.avoid_highways') }}</div>
                  </div>
                </label>
              </div>
              <div class="bg-white rounded-lg p-3 shadow-sm border">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="avoidFerries" @change="calculateRoute(true)" class="h-4 w-4 text-blue-600 mr-3" />
                  <div>
                    <div class="font-semibold text-xs text-gray-800">{{ $t('pv.shared.avoid_ferries') }}</div>
                  </div>
                </label>
              </div>

              <div v-if="routeData" class="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <h4 class="font-bold text-xs mb-2 text-blue-800 uppercase">📊 {{ $t('pv.gestione.travel_data') }}</h4>
                <div class="space-y-1 text-xs">
                  <div class="flex justify-between"><span>{{ $t('pv.shared.distance_label') }}</span><span class="font-bold text-blue-700">{{ routeData.distance }}</span></div>
                  <div class="flex justify-between"><span>{{ $t('pv.shared.duration_label') }}</span><span class="font-bold text-blue-700">{{ routeData.duration }}</span></div>
                </div>
              </div>

              <button @click="calculateRoute(true, true)" :disabled="isCalculatingRoute || isLoading || useHereMap" class="w-full bg-white border border-blue-600 text-blue-600 px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed">
                <v-icon name="hi-lightning-bolt" scale="0.8" /> {{ $t('pv.gestione.btn_optimize') }}
              </button>
              
              <button @click="calculateRoute(true)" :disabled="isCalculatingRoute || isLoading" class="w-full bg-white border border-blue-600 text-blue-600 px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2">
                <v-icon name="hi-refresh" scale="0.8" /> {{ $t('pv.gestione.btn_recalculate') }}
              </button>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 py-2 px-4 border-t flex justify-end gap-3">
          <button @click="closeMapModal" :disabled="isLoading" class="px-6 py-2 bg-gray-200 rounded-lg text-gray-700 font-bold text-xs">{{ $t('actions.cancel') }}</button>
          <button @click="saveUpdatedRoute" :disabled="isCalculatingRoute || isLoading || !routeData" class="px-8 py-2 bg-blue-600 rounded-lg text-white font-bold text-xs min-w-[160px]">
            {{ isLoading ? $t('pv.shared.saving') : $t('pv.gestione.btn_save_itinerary') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import draggable from 'vuedraggable';
import axios from 'axios';
import { router } from '@inertiajs/vue3';
import RouteEditorMapGoogle from '@/Pages/Modules/percorsi-vettori/RouteEditorMapGoogle.vue';
import RouteEditorMapHere from '@/Pages/Modules/percorsi-vettori/RouteEditorMapHere.vue';
import { createRoutingStrategy } from '@/Pages/Modules/percorsi-vettori/routingStrategies/routingFactory';

export default {
  components: { BreadCrumbs, draggable, RouteEditorMapGoogle, RouteEditorMapHere },
  props: {
    carriers: { type: Array, default: () => [] },
    group_by_companyName_and_address: { type: Boolean, default: false },
    usa_categoria_cliente: { type: String, default: '' },
    travels: { type: Object, default: () => ({ data: [], current_page: 1, last_page: 1, total: 0 }) },
    routing_provider: { type: String, default: 'google' },
    routing_provider_key: { type: String, default: '' },
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('pv.breadcrumb.percorsi_vettori'), href: route('modules.home', 'percorsi-vettori') },
        { name: this.$t('pv.breadcrumb.gestione_itinerari'), href: '#' }
      ],
      showMapModal: false,
      showOptionsPanel: false,
      selectedTravel: null,
      waypoints: [],
      mapCenter: { lat: 45.0732, lng: 7.6827 },
      isCalculatingRoute: false,
      optimizeRoute: false,
      avoidTolls: false,
      avoidHighways: false,
      avoidFerries: false,
      routeData: null,
      routeError: '',
      tempPlace: null,
      isLoading: false,
      expandedTravelId: null,
      initialLoadDone: false,
      routingStrategy: null,
      _routePayload: null,
    };
  },
  computed: {
    normalizedRoutingProvider() {
      const provider = String(this.routing_provider || '').toLowerCase().trim();
      return ['google', 'here'].includes(provider) ? provider : 'google';
    },
    useHereMap() {
      return this.normalizedRoutingProvider === 'here';
    },
    useGoogleMap() {
      return this.normalizedRoutingProvider === 'google';
    },
    paginationRange() {
      const current = this.travels.current_page;
      const last = this.travels.last_page;
      const range = 5;
      const halfRange = Math.floor(range / 2);
      
      let start = Math.max(1, current - halfRange);
      let end = Math.min(last, current + halfRange);
      
      if (end - start < range - 1) {
        if (start === 1) {
          end = Math.min(last, start + range - 1);
        } else {
          start = Math.max(1, end - range + 1);
        }
      }
      
      const pages = [];
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (end < last) {
        if (end < last - 1) pages.push('...');
        pages.push(last);
      }
      return pages;
    }
  },
  methods: {
    toggleTravel(id) { this.expandedTravelId = this.expandedTravelId === id ? null : id; },
    checkActiveOptions(options) {
      const opts = this.getOptions(options);
      return opts ? Object.values(opts).some(val => val === true) : false;
    },
    getOptions(options) {
      if (!options) return null;
      return typeof options === 'string' ? JSON.parse(options) : options;
    },

    onMapReady() {
      // Green-load: se il viaggio ha una polyline salvata la disegniamo
      // direttamente (nessuna chiamata di routing). Altrimenti ricalcoliamo.
      let rendered = false;
      if (!this.initialLoadDone && this.selectedTravel?.encoded_polyline) {
        rendered = this.$refs.routeMap?.renderSavedPolyline?.(this.selectedTravel.encoded_polyline) || false;
      }
      if (rendered) {
        this.initialLoadDone = true;
      } else {
        this.recalculate(false);
      }
    },

    async viewTravel(travel) {
      this.initialLoadDone = false;
      this._routePayload = null;
      this.routeError = '';
      this.selectedTravel = JSON.parse(JSON.stringify(travel));

      this.optimizeRoute = false;
      this.avoidTolls = false;
      this.avoidHighways = false;
      this.avoidFerries = false;

      // Carica le opzioni salvate
      if (travel.travel_options) {
        const opts = typeof travel.travel_options === 'string' ? JSON.parse(travel.travel_options) : travel.travel_options;
        this.optimizeRoute = opts.optimize_route || false;
        this.avoidTolls = opts.avoid_tolls || false;
        this.avoidHighways = opts.avoid_highways || false;
        this.avoidFerries = opts.avoid_ferries || false;
      }

      const rows = (travel.rows || []).map(row => {
        const lat = parseFloat(row.lat);
        const lng = parseFloat(row.lng);
        const docs = typeof row.documents_data === 'string' ? JSON.parse(row.documents_data) : row.documents_data;
        const telephon = Array.isArray(docs) ? [...new Set(docs.map(d => d.telephon).filter(Boolean))].join(' | ') : '';

        return {
          ...row,
          lat: isNaN(lat) ? null : lat,
          lng: isNaN(lng) ? null : lng,
          companyName: row.company_name,
          telephon: telephon,
          needsGeocoding: isNaN(lat) || isNaN(lng)
        };
      });

      this.waypoints = [
        { id: 'origin', address: travel.origin_address, company_name: this.$t('pv.shared.departure'), companyName: this.$t('pv.shared.departure'), isOrigin: true, needsGeocoding: true },
        ...rows,
        { id: 'destination', address: travel.destination_address ?? travel.origin_address, company_name: this.$t('pv.shared.arrival'), companyName: this.$t('pv.shared.arrival'), isDestination: true, needsGeocoding: true }
      ];

      this.routeData = {
        distance: (travel.total_km != null ? travel.total_km : 0) + ' km',
        duration: travel.total_duration || '-'
      };

      // Geocodifica origine/destinazione e centro mappa PRIMA di montare
      // il componente mappa: così eventuali ricalcoli successivi hanno le
      // coordinate pronte senza scatenare il watcher a componente non pronto.
      if (travel.origin_address) {
        await this.geocodeAndSetCenter(travel.origin_address);
      }
      await this.ensureGeocoded();

      this.showMapModal = true;
    },

    async ensureGeocoded() {
      if (!this.routingStrategy) return;
      const pending = this.waypoints.filter(wp => wp.needsGeocoding && wp.address);
      if (pending.length === 0) return;
      await this.routingStrategy.geocodeWaypoints(this.waypoints);
      this.waypoints.forEach(wp => {
        const lat = parseFloat(wp.lat);
        const lng = parseFloat(wp.lng);
        if (!isNaN(lat) && !isNaN(lng)) wp.needsGeocoding = false;
      });
    },

    calculateRoute(preserveView = false, optimize = false) {
      if (!this.waypoints || this.waypoints.length < 2) {
        this.routeError = this.$t('pv.generazione.route_error_min_stops');
        return;
      }
      this.routeError = '';
      this.isCalculatingRoute = true;
      this.initialLoadDone = true;
      this.optimizeRoute = optimize;
      this.$refs.routeMap?.calculateRoute?.(preserveView, optimize);
    },

    async recalculate(preserveView = false) {
      await this.ensureGeocoded();
      this.calculateRoute(preserveView);
    },

    onRouteCalculated(payload) {
      this.isCalculatingRoute = false;
      this.routeError = '';
      this._routePayload = payload;
      this.routeData = { distance: payload.distanceText, duration: payload.durationText };
      this.optimizeRoute = false;
    },

    onRouteError(error) {
      this.isCalculatingRoute = false;
      this.routeError = error?.message || this.$t('pv.shared.geocode_failed');
      console.error('[GestioneItinerari] route error:', error);
    },

    onWaypointsOptimized(optimizedWaypoints) {
      if (!Array.isArray(optimizedWaypoints) || optimizedWaypoints.length < 2) return;
      this.waypoints = optimizedWaypoints;
      this.optimizeRoute = false;
    },

    onAdjacentDuplicatesDetected() {
      this.isCalculatingRoute = false;
      console.warn('[GestioneItinerari] Tappe consecutive con stesso indirizzo (HERE).');
    },


    printTravel(travel) {
      const printUrl = route('modules.percorsi-vettori.print', { id: travel.id });
      window.open(printUrl, '_blank');
    },

    deleteTravel(travel) {
      if (!confirm(this.$t('pv.gestione.confirm_delete', { name: travel.travel_name }))) return;
      this.isLoading = true;
      axios.delete(route('modules.percorsi-vettori.delete', { id: travel.id }))
      .then(() => { 
          // Ricarica i dati della pagina Inertia
          this.$inertia.reload(); 
      })
      .catch((error) => { 
          console.error(error);
          alert(this.$t('pv.gestione.delete_error')); 
      })
      .finally(() => { 
          this.isLoading = false; 
      });

    },

    closeMapModal() {
      this.$refs.routeMap?.clearRoute?.();
      this.showMapModal = false;
      this.selectedTravel = null;
      this.waypoints = [];
      this.routeData = null;
      this._routePayload = null;
      this.initialLoadDone = false;
      this.tempPlace = null;
    },

    initAutocomplete() {
      if (!this.$refs.newWaypointInput || !this.routingStrategy) return;
      this.routingStrategy.initAutocomplete(this.$refs.newWaypointInput, (place) => {
        this.tempPlace = place;
      });
    },

    addNewWaypoint() {
      if (!this.tempPlace?.geometry) return;
      const name = this.tempPlace.name || this.$t('pv.shared.new_waypoint');
      const newWp = {
        id: 'man_' + Date.now(),
        address: this.tempPlace.formatted_address,
        company_name: name,
        companyName: name,
        lat: this.tempPlace.geometry.location.lat(),
        lng: this.tempPlace.geometry.location.lng(),
        isManual: true,
        needsGeocoding: false
      };
      this.waypoints.splice(this.waypoints.length - 1, 0, newWp);
      if (this.$refs.newWaypointInput) this.$refs.newWaypointInput.value = '';
      this.tempPlace = null;
      this.recalculate(true);
    },

    extractPolyline() {
      const p = this._routePayload;
      if (p) {
        if (p.provider === 'google' && p.raw?.routes?.[0]?.overview_polyline) return p.raw.routes[0].overview_polyline;
        if (p.provider === 'here' && p.raw?.sections?.[0]?.polyline) return p.raw.sections[0].polyline;
      }
      return this.selectedTravel?.encoded_polyline || '';
    },

    async saveUpdatedRoute() {
      if (!this.routeData) return alert(this.$t('pv.gestione.no_route_data'));
      this.isLoading = true;
      try {
        const polyline = this.extractPolyline();
        const mapsLink = this.routingStrategy ? this.routingStrategy.buildDeepLink(this.waypoints) : null;
        const payload = {
          travel_id: this.selectedTravel.id,
          travel_options: {
            optimize_route: this.optimizeRoute,
            avoid_tolls: this.avoidTolls,
            avoid_highways: this.avoidHighways,
            avoid_ferries: this.avoidFerries
          },
          maps_link: mapsLink,
          route_info: {
            distance: this.routeData.distance,
            duration: this.routeData.duration,
            polyline
          },
          origin_address: this.selectedTravel.origin_address,
          destination_address: this.selectedTravel.destination_address ?? this.selectedTravel.origin_address,
          travel_date: this.selectedTravel.travel_date,
          stops: this.waypoints.filter(wp => !wp.isOrigin && !wp.isDestination).map((wp, i) => ({
            ...wp, sort_order: i + 1
          }))
        };
        await axios.post(route('modules.percorsi-vettori.save-update'), payload);
        this.closeMapModal();
        this.$inertia.reload();
      } catch (e) { console.error(e); alert(this.$t('pv.gestione.save_error')); }
      finally { this.isLoading = false; }
    },

    onDragEnd() { this.recalculate(true); },
    removeWaypoint(i) {
      const wp = this.waypoints[i];
      if (wp.isOrigin || wp.isDestination) return;

      let docCount = 1;
      if (wp.documents_data) {
        try {
          const docs = typeof wp.documents_data === 'string' 
            ? JSON.parse(wp.documents_data) 
            : wp.documents_data;
          if (Array.isArray(docs)) docCount = docs.length;
        } catch(e) {}
      }

      if (docCount > 1) {
        if (!confirm(this.$t('pv.gestione.confirm_remove_grouped_docs', { count: docCount, name: wp.company_name }))) return;
      }

      this.waypoints.splice(i, 1);
      this.recalculate(true);
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('it-IT') : '-'; },
    getTelephonFromRow(row) {
      if (!row.documents_data) return '';
      const docs = typeof row.documents_data === 'string' ? JSON.parse(row.documents_data) : row.documents_data;
      if (!Array.isArray(docs)) return '';
      return [...new Set(docs.map(d => d.telephon).filter(Boolean))].join(' | ');
    },
    getCliCodesFromRow(row) {
      if (!row.documents_data) return '';
      const docs = typeof row.documents_data === 'string' ? JSON.parse(row.documents_data) : row.documents_data;
      if (!Array.isArray(docs)) return '';
      return [...new Set(docs.map(d => d.cli).filter(Boolean))].join(', ');
    },
    formatDuration(s) { const h = Math.floor(s/3600), m = Math.floor((s%3600)/60); return h > 0 ? `${h}h ${m}m` : `${m}m`; },
    
    async geocodeAndSetCenter(address) {
      if (!this.routingStrategy || !address) return;
      try {
        const coords = await this.routingStrategy.geocodeAddress(address);
        if (coords && coords.lat != null && coords.lng != null) {
          this.mapCenter = { lat: coords.lat, lng: coords.lng };
        }
      } catch (e) {
        console.error('[GestioneItinerari] geocodeAndSetCenter error:', e);
      }
    },

    exportExcel(travel) {
      const exportUrl = route('modules.percorsi-vettori.export', travel.id);
      window.open(exportUrl, '_blank');
    },

    getStaticMapUrl(travel) {
      if (!travel?.encoded_polyline) return '';
      // Immagine servita dal proxy interno: per HERE la flexible polyline viene
      // semplificata lato server per non superare il limite di lunghezza URL,
      // e la chiave resta server-side.
      return route('modules.percorsi-vettori.static-map', { id: travel.id, w: 800, h: 500 });
    },

    changePage(page) {
    router.get(
      route('modules.percorsi-vettori.gestione-itinerari'),
      { page },
      { only: ['travels'], preserveState: true, preserveScroll: true }
    );
},
  },

  watch: {
    showMapModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.initAutocomplete();
        });
      }
    },
    routing_provider() {
      this.routingStrategy = createRoutingStrategy(this.normalizedRoutingProvider, this.routing_provider_key);
      if (this.routingStrategy.preloadApi) this.routingStrategy.preloadApi();
    },
  },

  mounted() {
    this.routingStrategy = createRoutingStrategy(this.normalizedRoutingProvider, this.routing_provider_key);
    if (this.routingStrategy.preloadApi) this.routingStrategy.preloadApi();
  },
};
</script>

<style scoped>
.static-map-container img { aspect-ratio: 16 / 9; background-color: #f3f4f6; }
.pac-container { z-index: 10000 !important; }

:deep(.drag-ghost) {
  opacity: 0 !important;
  height: 3px !important;
  background: #3b82f6 !important;
  border-radius: 2px !important;
  border: none !important;
  padding: 0 !important;
  overflow: hidden !important;
}

:deep(.drag-chosen) {
  opacity: 1 !important;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.25) !important;
  transform: scale(1.02);
  z-index: 50;
}
</style>